import React from 'react';

import { Box, Grid } from '@mui/material';

import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { DateFormatPatterns } from '@clearverify/constants';
import { formatDate, formatPhoneNumber } from '@clearverify/utils';

import type { VerifierStaffItemFragment } from 'src/api';
import { UserStatus, useUpdateUserMutation } from 'src/api';
import { Button, DetailGridItem, FullscreenLoader } from 'src/components/common';
import { Field, Form, SelectField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useCurrentUser } from 'src/hooks/user';
import { enumToSelectOptions } from 'src/utils/common';
import { isEnableStatus } from 'src/utils/user';

import { DetailsFormFields, DetailsFormValues, StatusOption } from './types';
import { detailsFormValidationSchema } from './utils';

interface VerifierStaffViewDetailsByVerifierAdminModalFormProps {
  verifierStaff: VerifierStaffItemFragment | undefined;
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const VerifierStaffViewDetailsByVerifierAdminModalForm: React.FC<
  VerifierStaffViewDetailsByVerifierAdminModalFormProps
> = ({ verifierStaff, closeModal, switchClosability }) => {
  const { createApolloContext } = useNotification();

  const [updateUser] = useUpdateUserMutation({
    context: createApolloContext({ successMessage: 'Updated successfully' }),
  });
  const { user: currentUser, isUserLoading } = useCurrentUser();
  const initialValues: Partial<DetailsFormValues> = {
    status:
      verifierStaff?.user.status === UserStatus.Active
        ? StatusOption.Active
        : StatusOption.Inactive,
  };

  const onSubmit = React.useCallback(
    async (formValues: DetailsFormValues) => {
      switchClosability(false);

      const userId = verifierStaff?.user.id;

      try {
        await updateUser({
          variables: {
            where: { id: userId },
            data: {
              status:
                formValues.status === StatusOption.Active ? UserStatus.Active : UserStatus.Inactive,
            },
          },
        });

        switchClosability(true);
        closeModal();
      } catch {
        switchClosability(true);
      }
    },
    [closeModal, switchClosability, updateUser, verifierStaff?.user.id],
  );

  const { firstName, lastName, birthDate, phoneNumber, email, status } = verifierStaff?.user ?? {};

  const shouldRenderStatusField =
    !(verifierStaff?.user.id === currentUser?.id) && isEnableStatus(status);

  if (isUserLoading) {
    return <FullscreenLoader />;
  }

  return (
    <Form<DetailsFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      subscription={{ valid: true, submitting: true, dirty: true }}
      keepDirtyOnReinitialize
      validate={validateWithYupSchema(detailsFormValidationSchema)}
    >
      {({ submitting, valid, dirty }) => {
        return (
          <React.Fragment>
            <Grid container spacing={2}>
              <DetailGridItem topText="First Name" bottomText={firstName} />
              <DetailGridItem topText="Last Name" bottomText={lastName} />
              <DetailGridItem
                topText="Date of Birth"
                bottomText={formatDate(birthDate, DateFormatPatterns.shortDateWithSlash)}
              />
              <DetailGridItem topText="Phone Number" bottomText={formatPhoneNumber(phoneNumber)} />
              <DetailGridItem topText="Email Address" bottomText={email} />
            </Grid>

            {shouldRenderStatusField && (
              <Box mt={4}>
                <Field
                  name={DetailsFormFields.status}
                  label="Status"
                  component={SelectField}
                  fullWidth
                  options={enumToSelectOptions(StatusOption)}
                />
              </Box>
            )}

            <Box display="flex" gap={2} mt={4} justifyContent="center">
              <Button
                variant="outlined"
                color="error"
                onClick={() => closeModal()}
                disabled={submitting}
              >
                Close
              </Button>

              {!(verifierStaff?.user.id === currentUser?.id) && (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting || !dirty}
                  loading={submitting}
                >
                  Save Changes
                </Button>
              )}
            </Box>
          </React.Fragment>
        );
      }}
    </Form>
  );
};
